// src/app/slice/blogSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import blogService from "../../features/blog/service/blogService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/blog/types/blogTypes";

// Async Thunks
export const fetchBlogPosts = createAsyncThunk(
  "blog/fetchBlogPosts",
  async ({ filters = {}, pagination = {} }, { rejectWithValue }) => {
    try {
      const response = await blogService.getBlogPosts(filters, pagination);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchBlogPostById = createAsyncThunk(
  "blog/fetchBlogPostById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await blogService.getBlogPostById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const createBlogPost = createAsyncThunk(
  "blog/createBlogPost",
  async (postData, { rejectWithValue }) => {
    try {
      const response = await blogService.createBlogPost(postData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateBlogPost = createAsyncThunk(
  "blog/updateBlogPost",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await blogService.updateBlogPost(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteBlogPost = createAsyncThunk(
  "blog/deleteBlogPost",
  async (id, { rejectWithValue }) => {
    try {
      await blogService.deleteBlogPost(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const publishBlogPost = createAsyncThunk(
  "blog/publishBlogPost",
  async (id, { rejectWithValue }) => {
    try {
      const response = await blogService.publishBlogPost(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.PUBLISH_FAILED);
    }
  },
);

export const uploadBlogImage = createAsyncThunk(
  "blog/uploadBlogImage",
  async ({ file, path }, { rejectWithValue }) => {
    try {
      const response = await blogService.uploadImage(file, path);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPLOAD_FAILED);
    }
  },
);

export const fetchBlogStats = createAsyncThunk(
  "blog/fetchBlogStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await blogService.getBlogStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

const initialState = {
  posts: [],
  selectedPost: null,
  loading: false,
  error: null,
  success: null,
  pagination: {
    lastVisible: null,
    hasMore: false,
  },
  filters: {
    status: "all",
    category: "all",
    dateRange: { start: null, end: null },
    authorId: "all",
    search: "",
    tags: [],
  },
  sortBy: "createdAt",
  sortOrder: "desc",
  stats: {
    total: 0,
    draft: 0,
    published: 0,
    scheduled: 0,
    archived: 0,
    pending_review: 0,
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
    averageViews: 0,
    averageLikes: 0,
    averageComments: 0,
  },
};

const blogSlice = createSlice({
  name: "blog",
  initialState,
  reducers: {
    setPosts: (state, action) => {
      state.posts = action.payload;
    },
    setSelectedPost: (state, action) => {
      state.selectedPost = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSort: (state, action) => {
      state.sortBy = action.payload.sortBy || state.sortBy;
      state.sortOrder = action.payload.sortOrder || state.sortOrder;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetBlogState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Posts
      .addCase(fetchBlogPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = action.payload.posts;
        state.pagination = {
          lastVisible: action.payload.lastVisible,
          hasMore: action.payload.hasMore,
        };
      })
      .addCase(fetchBlogPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Post by ID
      .addCase(fetchBlogPostById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogPostById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPost = action.payload;
      })
      .addCase(fetchBlogPostById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Post
      .addCase(createBlogPost.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createBlogPost.fulfilled, (state, action) => {
        state.loading = false;
        state.posts.unshift(action.payload);
        state.selectedPost = action.payload;
        state.success = SUCCESS_MESSAGES.CREATE_SUCCESS;
      })
      .addCase(createBlogPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Post
      .addCase(updateBlogPost.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateBlogPost.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.posts.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.posts[index] = action.payload;
        }
        state.selectedPost = action.payload;
        state.success = SUCCESS_MESSAGES.UPDATE_SUCCESS;
      })
      .addCase(updateBlogPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Post
      .addCase(deleteBlogPost.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteBlogPost.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = state.posts.filter((p) => p.id !== action.payload);
        if (state.selectedPost?.id === action.payload) {
          state.selectedPost = null;
        }
        state.success = SUCCESS_MESSAGES.DELETE_SUCCESS;
      })
      .addCase(deleteBlogPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Publish Post
      .addCase(publishBlogPost.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(publishBlogPost.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.posts.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.posts[index] = action.payload;
        }
        if (state.selectedPost?.id === action.payload.id) {
          state.selectedPost = action.payload;
        }
        state.success = SUCCESS_MESSAGES.PUBLISH_SUCCESS;
      })
      .addCase(publishBlogPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Upload Image
      .addCase(uploadBlogImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadBlogImage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadBlogImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Stats
      .addCase(fetchBlogStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchBlogStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setPosts,
  setSelectedPost,
  setFilters,
  clearFilters,
  setSort,
  clearError,
  clearSuccess,
  resetBlogState,
} = blogSlice.actions;

export default blogSlice.reducer;
